import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import rolesService from '../../service/api/RolesService';
import usersService from '../../service/api/UsersService';
import { Tag } from 'primereact/tag';
import UserAdminDialog from './UserAdminDialog';
import { useTranslation } from 'react-i18next';
import { format, parseISO, toDate } from 'date-fns';
import { User } from '../../types/User';
import { Role } from '../../types/Role';

const emptyUser = {
  id: null,
};

export const UserManagement = () => {
  const [users, setUsers] = useState<User[]>(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const [roles, setUserRoles] = useState<Role[]>();
  const [selectedRowToEdit, setSelectedRowToEdit] = useState<User>(null);
  const [userDialogVisible, setUserDialogVisible] = useState(false);
  const { t } = useTranslation();

  const loadData = () => {
    setLoading(true);
    Promise.all([usersService.getAll(), rolesService.getAll()]).then((response) => {
      setUserRoles(response[1]);
      const rolesDict = Object.assign({}, ...response[1].map((r) => ({ [r.id]: r.name })));
      const users = response[0];
      users.forEach((u) => {
        u.roleName = rolesDict[u.roleId];
      });
      setUsers(users);
      setLoading(false);
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  const usersTableHeader = (
    <div className="table-header">
      <h3 className="m-0">{t('users.header')}</h3>
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder={t('common.search')}
        />
        <Button
          type="button"
          icon="pi pi-plus"
          label={t('common.add')}
          className="p-button ml-2"
          onClick={() => {
            addUser();
          }}
        ></Button>
      </span>
    </div>
  );

  const closeAddEditModal = () => {
    setUserDialogVisible(false);
    loadData();
  };

  const editUser = (_user: User) => {
    setSelectedRowToEdit(_user);
    setUserDialogVisible(true);
  };

  const addUser = () => {
    setSelectedRowToEdit(emptyUser as User);
    setUserDialogVisible(true);
  };

  const bodyTemplate = (data: User, props: ColumnBodyOptions) => {
    return <>{data[props.field]}</>;
  };

  const activeBodyTemplate = (data: User) => {
    return (
      <React.Fragment>
        {data.active === true ? (
          <Tag className="mr-2" icon="pi pi-check" severity="success" value={t('common.active')}></Tag>
        ) : (
          <Tag className="mr-2" icon="pi pi-times-circle" severity="danger" value={t('common.inactive')}></Tag>
        )}
      </React.Fragment>
    );
  };

  const readonlyBodyTemplate = (data: User) => {
    return (
      <React.Fragment>
        {data.readonly === true ? (
          <Tag className="mr-2" icon="pi pi-times-circle" severity="danger" value={t('users.readonly')}></Tag>
        ) : (
          <Tag className="mr-2" icon="pi pi-check" severity="success" value={t('users.write_access')}></Tag>
        )}
      </React.Fragment>
    );
  };

  const dateTemplate = (rowData: any): JSX.Element => (
    <>{format(toDate(parseISO(rowData.activeUntil)), t('formats.date_format'))}</>
  );

  const actionTemplate = (data: User) => (
    <span>
      <Button
        type="button"
        icon="pi pi-pencil"
        tooltip={t('common.edit')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success mr-2"
        onClick={() => {
          editUser(data);
        }}
      ></Button>
    </span>
  );

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <DataTable
            value={users}
            paginator
            className="p-datatable-customers"
            rows={10}
            dataKey="id"
            rowHover
            globalFilter={globalFilter}
            emptyMessage={t('users.users_empty_message')}
            loading={loading}
            header={usersTableHeader}
          >
            <Column field="email" header={t('users.email')} sortable body={bodyTemplate}></Column>
            <Column field="firstName" header={t('users.first_name')} sortable body={bodyTemplate}></Column>
            <Column field="lastName" header={t('users.last_name')} sortable body={bodyTemplate}></Column>
            <Column field="roleName" header={t('users.role')} sortable body={bodyTemplate}></Column>
            <Column field="activeUntil" header={t('users.active_until')} sortable body={dateTemplate}></Column>
            <Column
              field="active"
              header={t('common.active')}
              sortable
              body={activeBodyTemplate}
              align="center"
            ></Column>
            <Column
              field="readonly"
              header={t('users.readonly')}
              sortable
              body={readonlyBodyTemplate}
              align="center"
            ></Column>
            <Column
              headerStyle={{ width: '8rem' }}
              header={t('common.actions')}
              align="center"
              body={actionTemplate}
            ></Column>
          </DataTable>
          <UserAdminDialog
            userDialogVisible={userDialogVisible}
            closeAddEditModal={closeAddEditModal}
            user={selectedRowToEdit}
            availableRoles={roles}
          />
        </div>
      </div>
    </div>
  );
};
